import { useEffect, useMemo, useRef, useState } from "react";
import tw from "tailwind-styled-components";
import { FieldWrappers } from "../Styles/InputStyles";

const parseTime = (val) => {
    const str = String(val || "").trim();
    if (!str) return null;

    // 12-hour: "1:30 AM", "10:30 AM", "12:00 PM"
    const m12 = str.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)$/i);
    if (m12) {
        return {
            hour: String(parseInt(m12[1], 10)),
            minute: m12[2].padStart(2, "0"),
            meridiem: m12[3].toUpperCase(),
        };
    }

    // 24-hour: "23:59", "23:59:00", "9:05"
    const m24 = str.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
    if (m24) {
        let h = parseInt(m24[1], 10);
        const min = m24[2].padStart(2, "0");
        if (h < 0 || h > 23) return null;
        const meridiem = h >= 12 ? "PM" : "AM";
        let h12 = h % 12;
        if (h12 === 0) h12 = 12;
        return {
            hour: String(h12),
            minute: min,
            meridiem,
        };
    }

    return null;
};

const TimeBoxesInput = ({ value, onChange }) => {
    const parsedFromValue = useMemo(() => parseTime(value), [value]);

    const [hour, setHour] = useState(parsedFromValue?.hour ?? "");
    const [minute, setMinute] = useState(parsedFromValue?.minute ?? "");
    const [meridiem, setMeridiem] = useState(
        parsedFromValue?.meridiem ?? "AM"
    );

    const lastEmitted = useRef(value);

    useEffect(() => {
        if (value === lastEmitted.current) return;
        if (!parsedFromValue) {
            setHour("");
            setMinute("");
            setMeridiem("AM");
            lastEmitted.current = value;
            return;
        }
        setHour(parsedFromValue.hour);
        setMinute(parsedFromValue.minute);
        setMeridiem(parsedFromValue.meridiem);
        lastEmitted.current = value;
    }, [value]);

    const emit = (h, m, mer) => {
        if (h && m) {
            const hh = String(
                Math.min(Math.max(parseInt(h, 10) || 0, 1), 12)
            );
            const mm = String(
                Math.min(Math.max(parseInt(m, 10) || 0, 0), 59)
            ).padStart(2, "0");
            const out = `${hh}:${mm} ${mer}`;
            lastEmitted.current = out;
            onChange(out);
        } else if (!h && !m) {
            lastEmitted.current = "";
            onChange("");
        }
    };

    const handleHourChange = (e) => {
        const v = e.target.value.replace(/\D/g, "").slice(0, 2);
        setHour(v);
        emit(v, minute, meridiem);
    };

    const handleMinuteChange = (e) => {
        const v = e.target.value.replace(/\D/g, "").slice(0, 2);
        setMinute(v);
        emit(hour, v, meridiem);
    };

    const handleHourBlur = () => {
        if (!hour) return;
        const n = Math.min(Math.max(parseInt(hour, 10) || 1, 1), 12);
        const next = String(n);
        if (next !== hour) setHour(next);
        emit(next, minute, meridiem);
    };

    const handleMinuteBlur = () => {
        if (!minute) return;
        const n = Math.min(Math.max(parseInt(minute, 10) || 0, 0), 59);
        const padded = String(n).padStart(2, "0");
        if (padded !== minute) setMinute(padded);
        emit(hour, padded, meridiem);
    };

    const handleMeridiem = (mer) => {
        setMeridiem(mer);
        emit(hour, minute, mer);
    };

    return (
        <TimeRow>
            <FieldWrappers>
                <input
                    type="text"
                    inputMode="numeric"
                    placeholder="HH"
                    value={hour}
                    onChange={handleHourChange}
                    onBlur={handleHourBlur}
                    className="w-full border-none outline-none bg-transparent"
                />
            </FieldWrappers>

            <span className="text-gray-400 font-medium">:</span>

            <FieldWrappers>
                <input
                    type="text"
                    inputMode="numeric"
                    placeholder="MM"
                    value={minute}
                    onChange={handleMinuteChange}
                    onBlur={handleMinuteBlur}
                    className="w-full border-none outline-none bg-transparent"
                />
            </FieldWrappers>

            <MeridiemGroup>
                <MeridiemBtn
                    type="button"
                    $active={meridiem === "AM"}
                    onClick={() => handleMeridiem("AM")}
                >
                    AM
                </MeridiemBtn>
                <MeridiemBtn
                    type="button"
                    $active={meridiem === "PM"}
                    onClick={() => handleMeridiem("PM")}
                >
                    PM
                </MeridiemBtn>
            </MeridiemGroup>
        </TimeRow>
    );
};

const TimeRow = tw.div`
    flex items-center gap-2 w-full
`;

const MeridiemGroup = tw.div`
    flex items-center rounded-lg overflow-hidden
    border border-gray-200 h-12
    flex-none
`;

const MeridiemBtn = tw.button`
    px-3 h-full text-sm font-medium
    transition-colors
    ${(p) =>
        p.$active
            ? "bg-primary-color text-white"
            : "bg-white text-gray-600 hover:bg-gray-50"}
`;

export default TimeBoxesInput;